import React, { useState, useEffect } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import Icon from './Icon';
import {
  Btn,
  Card,
  Label,
  TextInput,
  TextArea,
  SelectInput,
  Section,
  StatusPill,
  useNow,
  fmtTime,
  fmtDate,
} from './ui';
import LocationPicker from './LocationPicker';
import './Dashboard.css';
import 'leaflet/dist/leaflet.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const NAV_ITEMS = [
  { id: 'einsaetze',    label: 'Einsätze',        icon: 'emergency' },
  { id: 'neu',          label: 'Neuer Einsatz',   icon: 'add_alert' },
  { id: 'termine',      label: 'Termine',         icon: 'event' },
  { id: 'einteilungen', label: 'Einteilungen',    icon: 'assignment_ind' },
  { id: 'fahrzeuge',    label: 'Fahrzeuge',       icon: 'fire_truck' },
  { id: 'karte',        label: 'Karte',           icon: 'map' },
  { id: 'audio',        label: 'Gong-Sound',      icon: 'volume_up' },
  { id: 'importexport', label: 'Import / Export', icon: 'import_export' },
];

const STICHWORT_PRESETS = [
  'B1 Müllbrand', 'B2 Zimmerbrand', 'B3 Wohnungsbrand', 'B4 Großbrand',
  'H1 Türöffnung', 'H1 Tier in Notlage', 'THL Person verletzt', 'THL VU PKW',
  'ABC Gefahrgut', 'TH Wasserschaden',
];

const HILFELEISTUNG_OPTIONS = [
  { value: 'Hilfeleistung',   label: 'Hilfeleistung' },
  { value: 'Brandeinsatz',    label: 'Brandeinsatz' },
  { value: 'Rettungseinsatz', label: 'Rettungseinsatz' },
  { value: 'ABC-Einsatz',     label: 'ABC-Einsatz' },
  { value: 'Sonstiges',       label: 'Sonstiges' },
];

const EINSATZ_ANLASS_OPTIONS = [
  { value: 'Mit Sondersignal',    label: 'Mit Sondersignal' },
  { value: 'Ohne Sondersignal',   label: 'Ohne Sondersignal' },
];

// ─── Sidebar ────────────────────────────────────────────────────────────────
const Sidebar = ({ active, onSelect, badges }) => (
  <aside className="dash-sidebar">
    <div className="dash-brand">
      <div className="dash-brand-logo">
        <Icon name="local_fire_department" size={24} color="#fff" />
      </div>
      <div>
        <div className="dash-brand-title">Einsatzleitung</div>
        <div className="dash-brand-sub">JF Ka/Kö</div>
      </div>
    </div>

    <div className="dash-nav">
      {NAV_ITEMS.map((item) => {
        const isActive = active === item.id;
        const badge = badges[item.id];
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            className={`dash-nav-item${isActive ? ' active' : ''}`}
          >
            {isActive && <span className="dash-nav-marker" />}
            <Icon name={item.icon} size={20} color={isActive ? 'var(--red)' : 'inherit'} />
            <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
            {badge > 0 && <span className="dash-nav-badge">{badge}</span>}
          </button>
        );
      })}
    </div>

    <div className="dash-sidebar-footer">
      <div className="dash-sidebar-footer-label">Ansichten</div>
      <a
        href="/monitor"
        target="_blank"
        rel="noopener noreferrer"
        className="dash-monitor-link"
      >
        <Icon name="open_in_new" size={16} color="var(--text-mute)" />
        Einsatzmonitor öffnen
      </a>
      <a
        href="/mobile"
        target="_blank"
        rel="noopener noreferrer"
        className="dash-monitor-link"
        style={{ marginTop: 10 }}
      >
        <Icon name="smartphone" size={16} color="var(--text-mute)" />
        Alarm-App öffnen
      </a>
    </div>
  </aside>
);

// ─── Topbar ─────────────────────────────────────────────────────────────────
const Topbar = ({ aktiveCount, erstellteCount, fahrzeuge }) => {
  const now = useNow(1000);
  const alarmActive = aktiveCount > 0;
  const bereit = fahrzeuge.filter((f) => f.status === 'bereit').length;
  return (
    <div className="dash-topbar">
      <div className="dash-topbar-stats">
        <Stat label="Aktiv" value={aktiveCount} color={alarmActive ? 'var(--red)' : 'var(--text-mute)'} pulse={alarmActive} />
        <div className="dash-topbar-divider" />
        <Stat label="Erstellt" value={erstellteCount} color={erstellteCount > 0 ? 'var(--amber)' : 'var(--text-mute)'} />
        <div className="dash-topbar-divider" />
        <Stat label="Bereit" value={bereit} sub={`/${fahrzeuge.length}`} color="var(--green)" />
      </div>
      <div className="dash-topbar-clock">
        {now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </div>
    </div>
  );
};

const Stat = ({ label, value, sub, color, pulse }) => (
  <div className="dash-stat">
    {pulse && <span className="dash-stat-pulse" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />}
    <div>
      <div className="dash-stat-label">{label}</div>
      <div className="dash-stat-value-row">
        <span style={{ color }}>{value}</span>
        {sub && <span className="dash-stat-sub">{sub}</span>}
      </div>
    </div>
  </div>
);

// ─── Einsatz-Card ───────────────────────────────────────────────────────────
const EinsatzCard = ({
  einsatz, fahrzeuge, onAusloesen, onBeenden, onErneut, onPDF, onDelete, onEdit,
}) => (
  <Card style={{ padding: 24, marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
    {einsatz.status === 'aktiv' && <div className="einsatz-card-stripe" />}
    <div className="einsatz-card-head">
      <StatusPill status={einsatz.status} />
      <span className="einsatz-card-time">
        {fmtDate(einsatz.timestamp)} · {fmtTime(einsatz.timestamp)}
      </span>
    </div>
    <h3 className="einsatz-card-title">{einsatz.stichwort}</h3>
    <div className="einsatz-card-address">
      <Icon name="location_on" size={16} />
      {einsatz.adresse}
    </div>
    {einsatz.beschreibung && <p className="einsatz-card-desc">{einsatz.beschreibung}</p>}
    <div className="einsatz-card-chips">
      {einsatz.alarmierteFahrzeuge.map((fid) => {
        const f = fahrzeuge.find((x) => x.id === fid);
        if (!f) return null;
        const alarmiert = einsatz.status === 'aktiv';
        return (
          <span key={fid} className={`einsatz-chip${alarmiert ? ' alarmiert' : ''}`}>
            {f.name}
          </span>
        );
      })}
      {einsatz.mitteiler && (
        <span className="einsatz-chip mitteiler">
          <Icon name="call" size={12} />
          {einsatz.mitteiler}
          {einsatz.telefon ? ` · ${einsatz.telefon}` : ''}
        </span>
      )}
    </div>
    <div className="einsatz-card-actions">
      {einsatz.status === 'erstellt' && (
        <Btn variant="primary" icon="notifications_active" onClick={() => onAusloesen(einsatz.id)}>
          Einsatz auslösen
        </Btn>
      )}
      {einsatz.status === 'aktiv' && (
        <Btn variant="success" icon="check" onClick={() => onBeenden(einsatz.id)}>
          Einsatz beenden
        </Btn>
      )}
      {einsatz.status === 'beendet' && (
        <Btn variant="primary" icon="refresh" onClick={() => onErneut(einsatz.id)}>
          Erneut auslösen
        </Btn>
      )}
      <Btn variant="secondary" icon="edit" onClick={() => onEdit(einsatz)}>Bearbeiten</Btn>
      <Btn variant="secondary" icon="picture_as_pdf" onClick={() => onPDF(einsatz.id)}>Alarmfax</Btn>
      <Btn variant="ghost" icon="delete" onClick={() => onDelete(einsatz.id)} style={{ marginLeft: 'auto' }}>
        Löschen
      </Btn>
    </div>
  </Card>
);

// ─── Einsatz-Edit-Form ──────────────────────────────────────────────────────
const EinsatzEditForm = ({ einsatz, fahrzeuge, kartenLocation, onSave, onCancel }) => {
  const [data, setData] = useState({
    stichwort: einsatz.stichwort || '',
    adresse: einsatz.adresse || '',
    ort: einsatz.ort || '',
    ortsteil: einsatz.ortsteil || '',
    hilfeleistung: einsatz.hilfeleistung || 'Hilfeleistung',
    einsatzAnlass: einsatz.einsatzAnlass || 'Mit Sondersignal',
    beschreibung: einsatz.beschreibung || '',
    mitteiler: einsatz.mitteiler || '',
    telefon: einsatz.telefon || '',
    alarmierteFahrzeuge: [...(einsatz.alarmierteFahrzeuge || [])],
    koordinaten: einsatz.koordinaten || kartenLocation,
  });
  const [showPicker, setShowPicker] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggle = (id) =>
    set('alarmierteFahrzeuge',
      data.alarmierteFahrzeuge.includes(id)
        ? data.alarmierteFahrzeuge.filter((x) => x !== id)
        : [...data.alarmierteFahrzeuge, id]
    );

  const submit = (e) => {
    e.preventDefault();
    onSave(data);
  };

  return (
    <Card style={{ padding: 24, marginBottom: 16, borderColor: 'var(--red)' }}>
      <form onSubmit={submit} className="dash-form-grid">
        <div className="dash-form-full">
          <Label>Stichwort</Label>
          <TextInput value={data.stichwort} onChange={(v) => set('stichwort', v)} />
        </div>
        <div>
          <Label>Straße / Hausnummer</Label>
          <TextInput value={data.adresse} onChange={(v) => set('adresse', v)} />
          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <Label>Ort</Label>
              <TextInput value={data.ort} onChange={(v) => set('ort', v)} placeholder="Stadtname" />
            </div>
            <div>
              <Label>Ortsteil</Label>
              <TextInput value={data.ortsteil} onChange={(v) => set('ortsteil', v)} placeholder="optional" />
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <Label>Mitteiler</Label>
            <TextInput value={data.mitteiler} onChange={(v) => set('mitteiler', v)} />
          </div>
          <div style={{ marginTop: 16 }}>
            <Label>Telefon</Label>
            <TextInput value={data.telefon} onChange={(v) => set('telefon', v)} />
          </div>
        </div>
        <div>
          <Label>Hilfeleistung</Label>
          <SelectInput value={data.hilfeleistung} onChange={(v) => set('hilfeleistung', v)} options={HILFELEISTUNG_OPTIONS} />
          <div style={{ marginTop: 16 }}>
            <Label>Einsatzanlass</Label>
            <SelectInput value={data.einsatzAnlass} onChange={(v) => set('einsatzAnlass', v)} options={EINSATZ_ANLASS_OPTIONS} />
          </div>
          <div style={{ marginTop: 16 }}>
            <Label>Beschreibung</Label>
          </div>
          <TextArea value={data.beschreibung} onChange={(v) => set('beschreibung', v)} rows={4} />
          <div style={{ marginTop: 16 }}>
            <Label>Einsatzort auf Karte</Label>
            <div className="dash-koord-row">
              <div className="dash-koord-text">
                {data.koordinaten
                  ? `${data.koordinaten.lat.toFixed(5)}, ${data.koordinaten.lng.toFixed(5)}`
                  : '—'}
              </div>
              <Btn variant="ghost" icon="my_location" onClick={() => setShowPicker(true)}>
                Ändern
              </Btn>
            </div>
          </div>
        </div>
        <div className="dash-form-full">
          <Label>Fahrzeuge alarmieren ({data.alarmierteFahrzeuge.length} / {fahrzeuge.length})</Label>
          <div className="dash-fz-grid">
            {fahrzeuge.map((f) => {
              const sel = data.alarmierteFahrzeuge.includes(f.id);
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => toggle(f.id)}
                  className={`dash-fz-card${sel ? ' selected' : ''}`}
                >
                  <div className="dash-fz-card-head">
                    <span className="dash-fz-card-name">{f.name}</span>
                    <Icon
                      name={sel ? 'check_circle' : 'radio_button_unchecked'}
                      size={18}
                      color={sel ? '#fff' : 'var(--text-dim)'}
                    />
                  </div>
                  <div className="dash-fz-card-funkruf">{f.funkruf || 'Funkruf —'}</div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="dash-form-full dash-form-actions">
          <Btn variant="ghost" onClick={onCancel}>Abbrechen</Btn>
          <Btn variant="primary" type="submit" icon="save">Speichern</Btn>
        </div>
      </form>
      <LocationPicker
        isVisible={showPicker}
        onLocationSelect={(loc) => {
          setData((d) => ({
            ...d,
            koordinaten: { lat: loc.lat, lng: loc.lng, zoom: loc.zoom },
            adresse: d.adresse?.trim() ? d.adresse : (loc.adresse || d.adresse),
          }));
          setShowPicker(false);
        }}
        onClose={() => setShowPicker(false)}
        initialLocation={data.koordinaten}
      />
    </Card>
  );
};

// ─── Page: Einsätze ─────────────────────────────────────────────────────────
const EinsaetzePage = ({ einsaetze, fahrzeuge, kartenLocation, onNew, actions }) => {
  const [filter, setFilter] = useState('alle');
  const [editId, setEditId] = useState(null);

  const filtered = einsaetze
    .filter((e) => filter === 'alle' || e.status === filter)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const counts = {
    alle: einsaetze.length,
    aktiv: einsaetze.filter((e) => e.status === 'aktiv').length,
    erstellt: einsaetze.filter((e) => e.status === 'erstellt').length,
    beendet: einsaetze.filter((e) => e.status === 'beendet').length,
  };

  return (
    <Section
      title="Einsätze"
      subtitle="Alle erstellten, aktiven und beendeten Einsätze"
      action={<Btn variant="primary" icon="add" onClick={onNew}>Neuer Einsatz</Btn>}
    >
      <div className="dash-filter-row">
        {['alle', 'aktiv', 'erstellt', 'beendet'].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`dash-filter-chip${filter === f ? ' active' : ''}`}
          >
            {f === 'alle' ? 'Alle' : f.charAt(0).toUpperCase() + f.slice(1)}
            <span className="dash-filter-count">{counts[f]}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card style={{ padding: 48, textAlign: 'center' }}>
          <Icon name="inbox" size={40} color="var(--text-dim)" />
          <div className="dash-empty">Keine Einsätze in dieser Ansicht</div>
        </Card>
      ) : (
        filtered.map((e) =>
          editId === e.id ? (
            <EinsatzEditForm
              key={e.id}
              einsatz={e}
              fahrzeuge={fahrzeuge}
              kartenLocation={kartenLocation}
              onCancel={() => setEditId(null)}
              onSave={async (data) => {
                await actions.editEinsatz(e.id, data);
                setEditId(null);
              }}
            />
          ) : (
            <EinsatzCard
              key={e.id}
              einsatz={e}
              fahrzeuge={fahrzeuge}
              onAusloesen={actions.ausloesen}
              onBeenden={actions.beenden}
              onErneut={actions.erneut}
              onPDF={actions.pdf}
              onDelete={actions.delete}
              onEdit={(einsatz) => setEditId(einsatz.id)}
            />
          )
        )
      )}
    </Section>
  );
};

// ─── Page: Neuer Einsatz ────────────────────────────────────────────────────
const NeuerEinsatzPage = ({ fahrzeuge, kartenLocation, onCreate, onCancel }) => {
  const [data, setData] = useState({
    stichwort: '',
    adresse: '',
    ort: '',
    ortsteil: '',
    hilfeleistung: 'Hilfeleistung',
    einsatzAnlass: 'Mit Sondersignal',
    beschreibung: '',
    mitteiler: '',
    telefon: '',
    alarmierteFahrzeuge: [],
    koordinaten: null,
  });
  const [showPicker, setShowPicker] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggle = (id) =>
    set('alarmierteFahrzeuge',
      data.alarmierteFahrzeuge.includes(id)
        ? data.alarmierteFahrzeuge.filter((x) => x !== id)
        : [...data.alarmierteFahrzeuge, id]
    );

  const canSubmit = data.stichwort && data.adresse && data.alarmierteFahrzeuge.length > 0;

  const reset = () => setData({
    stichwort: '', adresse: '', ort: '', ortsteil: '',
    hilfeleistung: 'Hilfeleistung', einsatzAnlass: 'Mit Sondersignal',
    beschreibung: '', mitteiler: '', telefon: '',
    alarmierteFahrzeuge: [], koordinaten: null,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    await onCreate({
      ...data,
      koordinaten: data.koordinaten || kartenLocation,
    });
    reset();
  };

  return (
    <Section
      title="Neuer Einsatz"
      subtitle="Erstellt einen Einsatz im Status „Erstellt“. Auslösen erfolgt anschließend separat."
    >
      <form onSubmit={submit} className="dash-form-grid">
        <Card style={{ padding: 24, gridColumn: '1 / -1' }}>
          <Label>Stichwort</Label>
          <TextInput
            value={data.stichwort}
            onChange={(v) => set('stichwort', v)}
            placeholder="z.B. B3 Wohnungsbrand"
          />
          <div className="dash-preset-row">
            {STICHWORT_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => set('stichwort', p)}
                className={`dash-preset-chip${data.stichwort === p ? ' active' : ''}`}
              >
                {p}
              </button>
            ))}
          </div>
        </Card>

        <Card style={{ padding: 24 }}>
          <Label>Straße / Hausnummer</Label>
          <TextInput
            value={data.adresse}
            onChange={(v) => set('adresse', v)}
            placeholder="Straße, Hausnummer"
          />
          <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <Label>Ort</Label>
              <TextInput
                value={data.ort}
                onChange={(v) => set('ort', v)}
                placeholder="Stadtname"
              />
            </div>
            <div>
              <Label>Ortsteil</Label>
              <TextInput
                value={data.ortsteil}
                onChange={(v) => set('ortsteil', v)}
                placeholder="Ortsteil (optional)"
              />
            </div>
          </div>
          <div style={{ marginTop: 20 }}>
            <Label>Mitteiler</Label>
            <TextInput
              value={data.mitteiler}
              onChange={(v) => set('mitteiler', v)}
              placeholder="Name / Behörde"
            />
          </div>
          <div style={{ marginTop: 20 }}>
            <Label>Telefon</Label>
            <TextInput
              value={data.telefon}
              onChange={(v) => set('telefon', v)}
              placeholder="Rückrufnummer"
            />
          </div>
        </Card>

        <Card style={{ padding: 24 }}>
          <Label>Hilfeleistung</Label>
          <SelectInput
            value={data.hilfeleistung}
            onChange={(v) => set('hilfeleistung', v)}
            options={HILFELEISTUNG_OPTIONS}
          />
          <div style={{ marginTop: 20 }}>
            <Label>Einsatzanlass</Label>
            <SelectInput
              value={data.einsatzAnlass}
              onChange={(v) => set('einsatzAnlass', v)}
              options={EINSATZ_ANLASS_OPTIONS}
            />
          </div>
          <div style={{ marginTop: 20 }}>
            <Label>Beschreibung</Label>
          </div>
          <TextArea
            value={data.beschreibung}
            onChange={(v) => set('beschreibung', v)}
            placeholder="Details zur Lage, Anzahl Verletzte, Besonderheiten…"
            rows={4}
          />
          <div style={{ marginTop: 20 }}>
            <Label>Einsatzort auf Karte</Label>
            <div className="dash-koord-row">
              <div className="dash-koord-text">
                {data.koordinaten
                  ? `${data.koordinaten.lat.toFixed(5)}, ${data.koordinaten.lng.toFixed(5)}`
                  : 'Aktuelle Karten-Position wird verwendet'}
              </div>
              <Btn variant="ghost" icon="my_location" onClick={() => setShowPicker(true)}>
                {data.koordinaten ? 'Ändern' : 'Wählen'}
              </Btn>
            </div>
          </div>
        </Card>

        <Card style={{ padding: 24, gridColumn: '1 / -1' }}>
          <div className="dash-fz-head">
            <Label>Fahrzeuge alarmieren</Label>
            <span className="dash-fz-count">
              {data.alarmierteFahrzeuge.length} / {fahrzeuge.length} ausgewählt
            </span>
          </div>
          <div className="dash-fz-grid">
            {fahrzeuge.map((f) => {
              const sel = data.alarmierteFahrzeuge.includes(f.id);
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => toggle(f.id)}
                  className={`dash-fz-card${sel ? ' selected' : ''}`}
                >
                  <div className="dash-fz-card-head">
                    <span className="dash-fz-card-name">{f.name}</span>
                    <Icon
                      name={sel ? 'check_circle' : 'radio_button_unchecked'}
                      size={18}
                      color={sel ? '#fff' : 'var(--text-dim)'}
                    />
                  </div>
                  <div className="dash-fz-card-funkruf">{f.funkruf || 'Funkruf —'}</div>
                </button>
              );
            })}
            {fahrzeuge.length === 0 && (
              <div className="dash-empty" style={{ gridColumn: '1 / -1' }}>
                Noch keine Fahrzeuge registriert.
              </div>
            )}
          </div>
        </Card>

        <div className="dash-form-actions dash-form-full">
          <Btn variant="ghost" onClick={() => { reset(); onCancel && onCancel(); }}>Verwerfen</Btn>
          <Btn variant="primary" type="submit" icon="save" disabled={!canSubmit}>
            Einsatz speichern
          </Btn>
        </div>
      </form>

      <LocationPicker
        isVisible={showPicker}
        onLocationSelect={(loc) => {
          setData((d) => ({
            ...d,
            koordinaten: { lat: loc.lat, lng: loc.lng, zoom: loc.zoom },
            // Adresse nur überschreiben, wenn noch leer
            adresse: d.adresse?.trim() ? d.adresse : (loc.adresse || d.adresse),
          }));
          setShowPicker(false);
        }}
        onClose={() => setShowPicker(false)}
        initialLocation={data.koordinaten || kartenLocation}
      />
    </Section>
  );
};

// ─── Page: Fahrzeuge ────────────────────────────────────────────────────────
const FahrzeugeCard = ({ fahrzeug, onEdit, onDelete }) => {
  const alarmiert = fahrzeug.status === 'alarmiert';
  return (
    <Card style={{ padding: 18 }}>
      <div className="dash-fz-item-head">
        <div>
          <div className="dash-fz-item-name">{fahrzeug.name}</div>
          <div className="dash-fz-item-funkruf">{fahrzeug.funkruf || '—'}</div>
        </div>
        <span className={`dash-fz-item-pill${alarmiert ? ' alarmiert' : ''}`}>
          <span className="dash-fz-item-dot" />
          {alarmiert ? 'Alarmiert' : 'Bereit'}
        </span>
      </div>
      <div className="dash-fz-item-actions">
        <Btn variant="ghost" icon="edit" onClick={() => onEdit(fahrzeug)}>Bearbeiten</Btn>
        <Btn variant="ghost" icon="delete" onClick={() => onDelete(fahrzeug.id)} style={{ marginLeft: 'auto' }}>
          Löschen
        </Btn>
      </div>
    </Card>
  );
};

const FahrzeugEditCard = ({ fahrzeug, kartenLocation, onSave, onCancel }) => {
  const [data, setData] = useState({
    name: fahrzeug.name || '',
    funkruf: fahrzeug.funkruf || '',
    position: fahrzeug.position || { lat: kartenLocation.lat, lng: kartenLocation.lng },
  });
  const [showPicker, setShowPicker] = useState(false);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  return (
    <Card style={{ padding: 18, borderColor: 'var(--red)' }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave(data);
        }}
        style={{ display: 'grid', gap: 14 }}
      >
        <div>
          <Label>Name</Label>
          <TextInput value={data.name} onChange={(v) => set('name', v)} />
        </div>
        <div>
          <Label>Funkruf</Label>
          <TextInput
            value={data.funkruf}
            onChange={(v) => set('funkruf', v)}
            placeholder="z.B. Florian Kö 1/44/1"
          />
        </div>
        <div>
          <Label>Position</Label>
          <div className="dash-koord-row">
            <div className="dash-koord-text">
              {data.position.lat.toFixed(5)}, {data.position.lng.toFixed(5)}
            </div>
            <Btn variant="ghost" icon="my_location" onClick={() => setShowPicker(true)}>
              Wählen
            </Btn>
          </div>
        </div>
        <div className="dash-form-actions">
          <Btn variant="ghost" onClick={onCancel}>Abbrechen</Btn>
          <Btn variant="primary" type="submit" icon="save">Speichern</Btn>
        </div>
      </form>
      <LocationPicker
        isVisible={showPicker}
        onLocationSelect={(loc) => {
          set('position', { lat: loc.lat, lng: loc.lng });
          setShowPicker(false);
        }}
        onClose={() => setShowPicker(false)}
        initialLocation={data.position}
      />
    </Card>
  );
};

const FahrzeugePage = ({ fahrzeuge, kartenLocation, actions }) => {
  const [newF, setNewF] = useState({ name: '', funkruf: '' });
  const [editId, setEditId] = useState(null);

  return (
    <Section
      title="Fahrzeuge"
      subtitle="Registrierte Einsatzfahrzeuge der Einheit"
    >
      <Card style={{ padding: 20, marginBottom: 24 }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!newF.name.trim()) return;
            actions.addFahrzeug(newF);
            setNewF({ name: '', funkruf: '' });
          }}
          className="dash-fz-add-row"
        >
          <div style={{ flex: 1 }}>
            <Label>Fahrzeugname</Label>
            <TextInput
              value={newF.name}
              onChange={(v) => setNewF({ ...newF, name: v })}
              placeholder="z.B. LF 20, RW, DLK 23"
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label>Funkruf (optional)</Label>
            <TextInput
              value={newF.funkruf}
              onChange={(v) => setNewF({ ...newF, funkruf: v })}
              placeholder="z.B. Florian Kö 1/44/1"
            />
          </div>
          <Btn variant="primary" icon="add" type="submit">Hinzufügen</Btn>
        </form>
      </Card>

      <div className="dash-fz-list-grid">
        {fahrzeuge.map((f) =>
          editId === f.id ? (
            <FahrzeugEditCard
              key={f.id}
              fahrzeug={f}
              kartenLocation={kartenLocation}
              onCancel={() => setEditId(null)}
              onSave={async (data) => {
                await actions.editFahrzeug(f.id, data);
                setEditId(null);
              }}
            />
          ) : (
            <FahrzeugeCard
              key={f.id}
              fahrzeug={f}
              onEdit={(fz) => setEditId(fz.id)}
              onDelete={actions.deleteFahrzeug}
            />
          )
        )}
        {fahrzeuge.length === 0 && (
          <Card style={{ padding: 32, textAlign: 'center' }}>
            <div className="dash-empty">Keine Fahrzeuge registriert</div>
          </Card>
        )}
      </div>
    </Section>
  );
};

// ─── Page: Karte ────────────────────────────────────────────────────────────
const ClickToPick = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
};

const MapCenterer = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView([center.lat, center.lng], zoom);
  }, [center.lat, center.lng, zoom, map]);
  return null;
};

const KartePage = ({ kartenLocation, onSave }) => {
  // Strings im State, damit Eingabe von "50.9" mitten im Tippen nicht reformatiert wird
  const [lat, setLat] = useState(String(kartenLocation.lat));
  const [lng, setLng] = useState(String(kartenLocation.lng));
  const [zoom, setZoom] = useState(String(kartenLocation.zoom));
  const [status, setStatus] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Sync von außen nur, wenn Nutzer nicht gerade tippt
  useEffect(() => {
    if (!dirty) {
      setLat(String(kartenLocation.lat));
      setLng(String(kartenLocation.lng));
      setZoom(String(kartenLocation.zoom));
    }
  }, [kartenLocation, dirty]);

  const numLat = parseFloat(lat);
  const numLng = parseFloat(lng);
  const numZoom = Math.max(1, Math.min(18, parseInt(zoom, 10) || 13));
  const previewLat = Number.isFinite(numLat) ? numLat : kartenLocation.lat;
  const previewLng = Number.isFinite(numLng) ? numLng : kartenLocation.lng;

  const handleSave = async () => {
    if (!Number.isFinite(numLat) || !Number.isFinite(numLng)) {
      setStatus({ type: 'error', msg: 'Bitte gültige Koordinaten eingeben.' });
      return;
    }
    setStatus({ type: 'info', msg: 'Wird gespeichert…' });
    try {
      await onSave({ lat: numLat, lng: numLng, zoom: numZoom });
      setStatus({ type: 'success', msg: 'Karten-Position gespeichert.' });
      setDirty(false);
      setTimeout(() => setStatus(null), 3000);
    } catch (e) {
      setStatus({ type: 'error', msg: 'Fehler beim Speichern: ' + (e.message || 'unbekannt') });
    }
  };

  const onMapPick = (p) => {
    setLat(String(p.lat.toFixed(6)));
    setLng(String(p.lng.toFixed(6)));
    setDirty(true);
    setStatus(null);
  };

  return (
    <Section title="Karte" subtitle="Standardkartenausschnitt für Monitor und Übersicht">
      <div className="dash-karte-grid">
        <Card style={{ padding: 24 }}>
          <Label>Breitengrad</Label>
          <TextInput value={lat} onChange={(v) => { setLat(v); setDirty(true); }} placeholder="50.9967" />
          <div style={{ marginTop: 16 }}>
            <Label>Längengrad</Label>
            <TextInput value={lng} onChange={(v) => { setLng(v); setDirty(true); }} placeholder="6.5711" />
          </div>
          <div style={{ marginTop: 16 }}>
            <Label>Zoom-Level (1–18)</Label>
            <TextInput type="number" value={zoom} onChange={(v) => { setZoom(v); setDirty(true); }} />
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <Btn variant="primary" icon="save" onClick={handleSave}>Speichern</Btn>
            {dirty && (
              <Btn
                variant="ghost"
                onClick={() => {
                  setLat(String(kartenLocation.lat));
                  setLng(String(kartenLocation.lng));
                  setZoom(String(kartenLocation.zoom));
                  setDirty(false);
                  setStatus(null);
                }}
              >
                Verwerfen
              </Btn>
            )}
          </div>
          {status && (
            <div className={`dash-upload-status ${status.type}`} style={{ marginTop: 14 }}>
              <Icon
                name={
                  status.type === 'success' ? 'check_circle' :
                  status.type === 'error'   ? 'error'        : 'info'
                }
                size={18}
              />
              {status.msg}
            </div>
          )}
          <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-mute)' }}>
            Tipp: Auf der Karte klicken, um Koordinaten zu setzen.
          </div>
        </Card>
        <Card style={{ height: 520, overflow: 'hidden' }}>
          <MapContainer
            center={[previewLat, previewLng]}
            zoom={numZoom}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap'
            />
            <Marker position={[previewLat, previewLng]} />
            <ClickToPick onPick={onMapPick} />
            <MapCenterer center={{ lat: previewLat, lng: previewLng }} zoom={numZoom} />
          </MapContainer>
        </Card>
      </div>
    </Section>
  );
};

// ─── Page: Audio (Gong-Einstellungen) ───────────────────────────────────────
const GongUploadBox = ({ id, file, onSelect, hasFile, label }) => (
  <div
    className="dash-upload-drop"
    onClick={() => document.getElementById(id).click()}
  >
    <Icon name="upload_file" size={36} color="var(--text-mute)" />
    <div className="dash-upload-title">
      {file ? file.name : hasFile ? `${label} ist hochgeladen — neu auswählen` : 'MP3-Datei auswählen'}
    </div>
    <div className="dash-upload-sub">Max 10 MB · MP3</div>
    <input
      id={id}
      type="file"
      accept="audio/mp3,audio/mpeg"
      style={{ display: 'none' }}
      onChange={(e) => onSelect(e.target.files[0])}
    />
  </div>
);

const GongCard = ({
  title, subtitle, inputId, file, onSelect, hasFile,
  uploadUrl, testKey,
  volume, onVolumeChange, onVolumeCommit,
  loop, onLoopToggle,
  status, setStatus, onCommitSettings, refreshStatus,
}) => {
  const upload = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append('gongAudio', file);
    setStatus({ type: 'info', msg: 'Wird hochgeladen…' });
    try {
      const r = await axios.post(uploadUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data.success) {
        setStatus({ type: 'success', msg: 'Gong erfolgreich hochgeladen!' });
        onSelect(null);
        const inp = document.getElementById(inputId);
        if (inp) inp.value = '';
        await refreshStatus();
      }
    } catch (e) {
      setStatus({ type: 'error', msg: e.response?.data?.error || 'Upload-Fehler.' });
    }
  };

  const playTest = async () => {
    // Lokal sofort abspielen (Dashboard-Tab ist durch den Klick freigeschaltet)
    try {
      const url = testKey === 'termin' ? '/api/termin-gong-audio' : '/api/gong-audio';
      const audio = new Audio(`${url}?t=${Date.now()}`);
      audio.volume = Math.max(0, Math.min(1, volume));
      audio.loop = false; // Test immer ohne Loop
      audio.play().catch((err) => {
        if (err && err.name === 'NotSupportedError') {
          setStatus({ type: 'error', msg: 'Keine Audio-Datei hochgeladen.' });
        } else {
          console.error('Lokales Test-Audio Fehler:', err);
        }
      });
    } catch (e) {
      console.error(e);
    }
    // Zusätzlich Monitor-Broadcast (falls Monitor offen ist, hört er da auch)
    try { await axios.post('/api/gong-test', { which: testKey }); } catch (e) { /* ignore */ }
  };

  return (
    <Card style={{ padding: 24 }}>
      <div className="gong-card-head">
        <div>
          <h3 className="gong-card-title">{title}</h3>
          <div className="gong-card-sub">{subtitle}</div>
        </div>
        <span className={`gong-status-pill${hasFile ? ' ok' : ''}`}>
          <Icon name={hasFile ? 'check_circle' : 'warning'} size={14} />
          {hasFile ? 'Datei vorhanden' : 'Keine Datei'}
        </span>
      </div>

      <GongUploadBox
        id={inputId}
        file={file}
        onSelect={onSelect}
        hasFile={hasFile}
        label={title}
      />

      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <Btn variant="primary" icon="cloud_upload" disabled={!file} onClick={upload}>
          Hochladen
        </Btn>
        <Btn variant="secondary" icon="play_arrow" disabled={!hasFile} onClick={playTest}>
          Test
        </Btn>
      </div>

      <div className="gong-control-row">
        <div style={{ flex: 1, minWidth: 200 }}>
          <Label>Lautstärke ({Math.round(volume * 100)} %)</Label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
            onMouseUp={onVolumeCommit}
            onTouchEnd={onVolumeCommit}
            className="gong-slider"
          />
        </div>
        {onLoopToggle && (
          <div>
            <Label>Endlos-Wiederholung</Label>
            <button
              type="button"
              onClick={() => { onLoopToggle(); onCommitSettings(); }}
              className={`gong-toggle${loop ? ' on' : ''}`}
            >
              <Icon name={loop ? 'repeat_on' : 'repeat'} size={18} />
              {loop ? 'Loop aktiv' : 'Loop aus'}
            </button>
          </div>
        )}
      </div>

      {status && (
        <div className={`dash-upload-status ${status.type}`} style={{ marginTop: 14 }}>
          <Icon
            name={status.type === 'success' ? 'check_circle' : status.type === 'error' ? 'error' : 'info'}
            size={18}
          />
          {status.msg}
        </div>
      )}
    </Card>
  );
};

const AudioPage = () => {
  const [settings, setSettings] = useState({
    alarmVolume: 0.8,
    alarmLoop: false,
    terminVolume: 0.8,
    hasAlarm: false,
    hasTermin: false,
  });
  const [alarmFile, setAlarmFile] = useState(null);
  const [terminFile, setTerminFile] = useState(null);
  const [alarmStatus, setAlarmStatus] = useState(null);
  const [terminStatus, setTerminStatus] = useState(null);
  const [globalStatus, setGlobalStatus] = useState(null);

  const refresh = async () => {
    try {
      const r = await axios.get('/api/gong-settings');
      setSettings((s) => ({ ...s, ...r.data }));
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    refresh();
    const socket = io();
    socket.on('gongSettingsUpdated', (s) => setSettings((prev) => ({ ...prev, ...s })));
    socket.on('gongStatusUpdated', (s) => setSettings((prev) => ({ ...prev, ...s })));
    return () => socket.close();
  }, []);

  const commitSettings = async (next) => {
    try {
      await axios.post('/api/gong-settings', next);
    } catch (e) {
      setGlobalStatus({ type: 'error', msg: 'Einstellungen konnten nicht gespeichert werden.' });
    }
  };

  const updateLocal = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const stopAll = async () => {
    try {
      await axios.post('/api/gong-stop');
      setGlobalStatus({ type: 'success', msg: 'Alle Gongs gestoppt.' });
      setTimeout(() => setGlobalStatus(null), 2500);
    } catch (e) {
      setGlobalStatus({ type: 'error', msg: 'Stoppen fehlgeschlagen.' });
    }
  };

  return (
    <Section
      title="Gong-Sound"
      subtitle="Audio-Signale für Alarm und Termine. Lautstärke und Loop werden serverseitig gespeichert und gelten für alle Monitore."
      action={
        <Btn variant="danger" icon="stop_circle" onClick={stopAll}>
          Alle Gongs sofort stoppen
        </Btn>
      }
    >
      {globalStatus && (
        <div className={`dash-upload-status ${globalStatus.type}`} style={{ marginBottom: 20 }}>
          <Icon
            name={globalStatus.type === 'success' ? 'check_circle' : 'error'}
            size={18}
          />
          {globalStatus.msg}
        </div>
      )}

      <div className="gong-grid">
        <GongCard
          title="Alarm-Gong"
          subtitle="Wird beim Auslösen eines Einsatzes abgespielt."
          inputId="alarm-gong-upload"
          file={alarmFile}
          onSelect={setAlarmFile}
          hasFile={!!settings.hasAlarm}
          uploadUrl="/api/upload-gong"
          testKey="alarm"
          volume={settings.alarmVolume}
          onVolumeChange={(v) => updateLocal({ alarmVolume: v })}
          onVolumeCommit={() => commitSettings({ alarmVolume: settings.alarmVolume })}
          loop={settings.alarmLoop}
          onLoopToggle={() => updateLocal({ alarmLoop: !settings.alarmLoop })}
          onCommitSettings={() => commitSettings({ alarmLoop: !settings.alarmLoop })}
          status={alarmStatus}
          setStatus={setAlarmStatus}
          refreshStatus={refresh}
        />

        <GongCard
          title="Termin-Gong"
          subtitle="Wird automatisch abgespielt, wenn die Uhrzeit eines Termins erreicht ist."
          inputId="termin-gong-upload"
          file={terminFile}
          onSelect={setTerminFile}
          hasFile={!!settings.hasTermin}
          uploadUrl="/api/upload-termin-gong"
          testKey="termin"
          volume={settings.terminVolume}
          onVolumeChange={(v) => updateLocal({ terminVolume: v })}
          onVolumeCommit={() => commitSettings({ terminVolume: settings.terminVolume })}
          onCommitSettings={() => {}}
          status={terminStatus}
          setStatus={setTerminStatus}
          refreshStatus={refresh}
        />
      </div>
    </Section>
  );
};

// ─── Page: Termine ──────────────────────────────────────────────────────────
const TermineForm = ({ initial, onSave, onCancel, submitLabel = 'Speichern' }) => {
  const [data, setData] = useState({
    titel: initial?.titel || '',
    uhrzeit: initial?.uhrzeit || '',
    beschreibung: initial?.beschreibung || '',
  });
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const canSubmit = data.titel.trim() && /^\d{1,2}:\d{2}$/.test(data.uhrzeit);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        // Uhrzeit normalisieren: "9:5" → "09:05"
        const [hStr, mStr] = data.uhrzeit.split(':');
        const uhrzeit = `${String(parseInt(hStr, 10)).padStart(2, '0')}:${String(parseInt(mStr, 10)).padStart(2, '0')}`;
        onSave({ ...data, uhrzeit });
      }}
      style={{ display: 'grid', gap: 16 }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 16 }}>
        <div>
          <Label>Titel</Label>
          <TextInput
            value={data.titel}
            onChange={(v) => set('titel', v)}
            placeholder="z.B. Fahrzeugeinteilung"
          />
        </div>
        <div>
          <Label>Uhrzeit</Label>
          <TextInput
            type="time"
            value={data.uhrzeit}
            onChange={(v) => set('uhrzeit', v)}
            placeholder="09:00"
          />
        </div>
      </div>
      <div>
        <Label>Beschreibung (optional)</Label>
        <TextArea
          value={data.beschreibung}
          onChange={(v) => set('beschreibung', v)}
          placeholder="Details, Ort, etc."
          rows={3}
        />
      </div>
      <div className="dash-form-actions">
        {onCancel && (
          <Btn variant="ghost" onClick={onCancel}>Abbrechen</Btn>
        )}
        <Btn variant="primary" type="submit" icon="save" disabled={!canSubmit}>
          {submitLabel}
        </Btn>
      </div>
    </form>
  );
};

const isTerminVergangen = (uhrzeit, now) => {
  if (!uhrzeit) return false;
  const [h, m] = uhrzeit.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return false;
  const total = h * 60 + m;
  const nowTotal = now.getHours() * 60 + now.getMinutes();
  return total < nowTotal;
};

const TermineCard = ({ termin, vergangen, onEdit, onDelete }) => (
  <Card style={{ padding: 18, opacity: vergangen ? 0.5 : 1 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div className="termin-uhrzeit">{termin.uhrzeit}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="termin-titel">{termin.titel}</div>
        {termin.beschreibung && (
          <div className="termin-beschreibung">{termin.beschreibung}</div>
        )}
      </div>
      <div className="termin-actions">
        <Btn variant="ghost" icon="edit" onClick={() => onEdit(termin)}>Bearbeiten</Btn>
        <Btn variant="ghost" icon="delete" onClick={() => onDelete(termin.id)}>Löschen</Btn>
      </div>
    </div>
  </Card>
);

const TerminePage = ({ termine, actions }) => {
  const [editId, setEditId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const now = useNow(30000);

  const sorted = [...termine].sort((a, b) => (a.uhrzeit || '').localeCompare(b.uhrzeit || ''));
  const anstehend = sorted.filter((t) => !isTerminVergangen(t.uhrzeit, now));
  const vergangen = sorted.filter((t) => isTerminVergangen(t.uhrzeit, now));

  return (
    <Section
      title="Termine"
      subtitle="Tagesplanung für alle. Vergangene Termine werden auf dem Monitor ausgeblendet."
      action={
        !showNew && (
          <Btn variant="primary" icon="add" onClick={() => setShowNew(true)}>
            Neuer Termin
          </Btn>
        )
      }
    >
      {showNew && (
        <Card style={{ padding: 24, marginBottom: 20, borderColor: 'var(--red)' }}>
          <TermineForm
            onCancel={() => setShowNew(false)}
            onSave={async (data) => {
              await actions.addTermin(data);
              setShowNew(false);
            }}
            submitLabel="Termin speichern"
          />
        </Card>
      )}

      {anstehend.length === 0 && vergangen.length === 0 && !showNew && (
        <Card style={{ padding: 48, textAlign: 'center' }}>
          <Icon name="event_available" size={40} color="var(--text-dim)" />
          <div className="dash-empty">Noch keine Termine geplant</div>
        </Card>
      )}

      {anstehend.length > 0 && (
        <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
          {anstehend.map((t) =>
            editId === t.id ? (
              <Card key={t.id} style={{ padding: 24, borderColor: 'var(--red)' }}>
                <TermineForm
                  initial={t}
                  onCancel={() => setEditId(null)}
                  onSave={async (data) => {
                    await actions.editTermin(t.id, data);
                    setEditId(null);
                  }}
                />
              </Card>
            ) : (
              <TermineCard
                key={t.id}
                termin={t}
                vergangen={false}
                onEdit={(x) => setEditId(x.id)}
                onDelete={actions.deleteTermin}
              />
            )
          )}
        </div>
      )}

      {vergangen.length > 0 && (
        <>
          <div className="termin-section-label">Vergangen ({vergangen.length})</div>
          <div style={{ display: 'grid', gap: 12 }}>
            {vergangen.map((t) =>
              editId === t.id ? (
                <Card key={t.id} style={{ padding: 24, borderColor: 'var(--red)' }}>
                  <TermineForm
                    initial={t}
                    onCancel={() => setEditId(null)}
                    onSave={async (data) => {
                      await actions.editTermin(t.id, data);
                      setEditId(null);
                    }}
                  />
                </Card>
              ) : (
                <TermineCard
                  key={t.id}
                  termin={t}
                  vergangen
                  onEdit={(x) => setEditId(x.id)}
                  onDelete={actions.deleteTermin}
                />
              )
            )}
          </div>
        </>
      )}
    </Section>
  );
};

// ─── Page: Import / Export ──────────────────────────────────────────────────
const ImportExportPage = ({ fahrzeuge, einsaetze, termine, einteilungen, onImportSuccess }) => {
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [importMode, setImportMode] = useState('ersetzen');
  const [status, setStatus] = useState(null);

  const handleExport = () => {
    window.open('/api/export', '_blank');
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    setImportFile(file);
    setImportData(null);
    setStatus(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (!Array.isArray(data.fahrzeuge) || !Array.isArray(data.einsaetze) || !Array.isArray(data.termine)) {
          setStatus({ type: 'error', msg: 'Ungültiges Format: fahrzeuge, einsaetze und termine müssen vorhanden sein.' });
          return;
        }
        setImportData(data);
      } catch {
        setStatus({ type: 'error', msg: 'Ungültige JSON-Datei — konnte nicht gelesen werden.' });
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importData) return;
    setStatus({ type: 'info', msg: 'Wird importiert…' });
    try {
      const r = await axios.post('/api/import', { ...importData, mode: importMode });
      const { fahrzeuge: f, einsaetze: e, termine: t, einteilungen: et } = r.data.imported;
      const etPart = et > 0 ? `, ${et} Einteilungen` : '';
      setStatus({ type: 'success', msg: `Import erfolgreich — ${f} Fahrzeuge, ${e} Einsätze, ${t} Termine${etPart}.` });
      setImportFile(null);
      setImportData(null);
      onImportSuccess();
    } catch (err) {
      setStatus({ type: 'error', msg: err.response?.data?.error || 'Import fehlgeschlagen.' });
    }
  };

  const fmtTs = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('de-DE'); } catch { return iso; }
  };

  return (
    <Section
      title="Import / Export"
      subtitle="Daten als JSON-Datei sichern und wiederherstellen"
    >
      <div className="ie-grid">
        {/* ── Export ── */}
        <Card style={{ padding: 24 }}>
          <div className="ie-card-head">
            <Icon name="download" size={28} color="var(--green)" />
            <div>
              <div className="ie-card-title">Exportieren</div>
              <div className="ie-card-sub">Aktuellen Datenstand als JSON herunterladen</div>
            </div>
          </div>

          <div className="ie-summary">
            <div className="ie-summary-row">
              <Icon name="fire_truck" size={16} color="var(--text-mute)" />
              <span className="ie-summary-label">Fahrzeuge</span>
              <span className="ie-summary-val">{fahrzeuge.length}</span>
            </div>
            <div className="ie-summary-row">
              <Icon name="emergency" size={16} color="var(--text-mute)" />
              <span className="ie-summary-label">Einsätze</span>
              <span className="ie-summary-val">{einsaetze.length}</span>
            </div>
            <div className="ie-summary-row">
              <Icon name="event" size={16} color="var(--text-mute)" />
              <span className="ie-summary-label">Termine</span>
              <span className="ie-summary-val">{termine.length}</span>
            </div>
            <div className="ie-summary-row">
              <Icon name="assignment_ind" size={16} color="var(--text-mute)" />
              <span className="ie-summary-label">Einteilungen</span>
              <span className="ie-summary-val">{(einteilungen || []).length}</span>
            </div>
          </div>

          <Btn variant="success" icon="download" onClick={handleExport} style={{ marginTop: 20, width: '100%' }}>
            Jetzt exportieren
          </Btn>
        </Card>

        {/* ── Import ── */}
        <Card style={{ padding: 24 }}>
          <div className="ie-card-head">
            <Icon name="upload" size={28} color="var(--amber)" />
            <div>
              <div className="ie-card-title">Importieren</div>
              <div className="ie-card-sub">JSON-Datei auswählen und Daten laden</div>
            </div>
          </div>

          <div
            className="dash-upload-drop"
            onClick={() => document.getElementById('ie-file-input').click()}
          >
            <Icon name="upload_file" size={36} color="var(--text-mute)" />
            <div className="dash-upload-title">
              {importFile ? importFile.name : 'JSON-Datei auswählen oder ablegen'}
            </div>
            <div className="dash-upload-sub">alarmmonitor-export-*.json</div>
            <input
              id="ie-file-input"
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files[0])}
            />
          </div>

          {importData && (
            <div className="ie-preview-box">
              <div className="ie-preview-title">
                <Icon name="check_circle" size={16} color="var(--green)" />
                Datei erkannt
              </div>
              {importData.exportedAt && (
                <div className="ie-preview-row">
                  <span>Exportiert am</span>
                  <span>{fmtTs(importData.exportedAt)}</span>
                </div>
              )}
              <div className="ie-preview-row">
                <span>Fahrzeuge</span>
                <span>{importData.fahrzeuge.length}</span>
              </div>
              <div className="ie-preview-row">
                <span>Einsätze</span>
                <span>{importData.einsaetze.length}</span>
              </div>
              <div className="ie-preview-row">
                <span>Termine</span>
                <span>{importData.termine.length}</span>
              </div>
              {Array.isArray(importData.einteilungen) && importData.einteilungen.length > 0 && (
                <div className="ie-preview-row">
                  <span>Einteilungen</span>
                  <span>{importData.einteilungen.length}</span>
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <Label>Import-Modus</Label>
            <div className="ie-mode-row">
              <button
                type="button"
                className={`ie-mode-btn${importMode === 'ersetzen' ? ' active' : ''}`}
                onClick={() => setImportMode('ersetzen')}
              >
                <Icon name="swap_horiz" size={16} />
                Ersetzen
              </button>
              <button
                type="button"
                className={`ie-mode-btn${importMode === 'zusammenfuehren' ? ' active' : ''}`}
                onClick={() => setImportMode('zusammenfuehren')}
              >
                <Icon name="merge" size={16} />
                Zusammenführen
              </button>
            </div>
            <div className="ie-mode-hint">
              {importMode === 'ersetzen'
                ? 'Alle aktuellen Daten werden durch die importierten ersetzt.'
                : 'Importierte Daten werden zu den bestehenden hinzugefügt (neue IDs).'}
            </div>
          </div>

          <Btn
            variant="primary"
            icon="upload"
            disabled={!importData}
            onClick={handleImport}
            style={{ marginTop: 16, width: '100%' }}
          >
            Importieren
          </Btn>

          {status && (
            <div className={`dash-upload-status ${status.type}`} style={{ marginTop: 14 }}>
              <Icon
                name={status.type === 'success' ? 'check_circle' : status.type === 'error' ? 'error' : 'info'}
                size={18}
              />
              {status.msg}
            </div>
          )}
        </Card>
      </div>
    </Section>
  );
};

// ─── Einteilungen ───────────────────────────────────────────────────────────

const PersonenEditor = ({ personen, onChange }) => {
  const add = () => onChange([...personen, { name: '', funktion: '' }]);
  const remove = (i) => onChange(personen.filter((_, idx) => idx !== i));
  const set = (i, field, val) => onChange(personen.map((p, idx) => idx === i ? { ...p, [field]: val } : p));

  return (
    <div className="et-personen-editor">
      {personen.map((p, i) => (
        <div key={i} className="et-personen-row">
          <TextInput
            value={p.name}
            onChange={v => set(i, 'name', v)}
            placeholder="Name"
          />
          <TextInput
            value={p.funktion}
            onChange={v => set(i, 'funktion', v)}
            placeholder="Funktion (optional)"
          />
          <button type="button" className="et-personen-remove" onClick={() => remove(i)} title="Entfernen">
            <Icon name="close" size={16} />
          </button>
        </div>
      ))}
      <button type="button" className="et-personen-add" onClick={add}>
        <Icon name="add" size={16} />
        Person hinzufügen
      </button>
    </div>
  );
};

const EinteilungForm = ({ initial, fahrzeuge, onSave, onCancel, submitLabel }) => {
  const [name, setName] = useState(initial?.name || '');
  const [beschreibung, setBeschreibung] = useState(initial?.beschreibung || '');
  const [zuweisungen, setZuweisungen] = useState(() => {
    if (initial?.fahrzeugZuweisungen?.length) return initial.fahrzeugZuweisungen;
    return [];
  });

  const isSelected = (fzId) => zuweisungen.some(z => z.fahrzeugId === fzId);

  const toggleFahrzeug = (fzId) => {
    if (isSelected(fzId)) {
      setZuweisungen(zuweisungen.filter(z => z.fahrzeugId !== fzId));
    } else {
      setZuweisungen([...zuweisungen, { fahrzeugId: fzId, personen: [] }]);
    }
  };

  const updatePersonen = (fzId, personen) => {
    setZuweisungen(zuweisungen.map(z => z.fahrzeugId === fzId ? { ...z, personen } : z));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) { alert('Name ist erforderlich.'); return; }
    onSave({ name: name.trim(), beschreibung: beschreibung.trim(), fahrzeugZuweisungen: zuweisungen });
  };

  return (
    <form onSubmit={submit}>
      <Card style={{ padding: 24, marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <Label>Name <span style={{ color: 'var(--red)' }}>*</span></Label>
            <TextInput value={name} onChange={setName} placeholder="z.B. Übungsabend 28.05." />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Label>Beschreibung (optional)</Label>
            <TextInput value={beschreibung} onChange={setBeschreibung} placeholder="z.B. Abschnitt West" />
          </div>
        </div>
      </Card>

      <Card style={{ padding: 24, marginBottom: 16 }}>
        <Label>Fahrzeuge & Besatzung</Label>
        {fahrzeuge.length === 0 && (
          <div className="dash-empty-state" style={{ padding: '16px 0' }}>
            <Icon name="fire_truck" size={28} color="var(--text-dim)" />
            <div>Keine Fahrzeuge vorhanden — zuerst Fahrzeuge anlegen.</div>
          </div>
        )}
        <div className="et-fz-list" style={{ marginTop: 12 }}>
          {fahrzeuge.map(fz => {
            const sel = isSelected(fz.id);
            const zuweisung = zuweisungen.find(z => z.fahrzeugId === fz.id);
            return (
              <div key={fz.id} className={`et-fz-block${sel ? ' selected' : ''}`}>
                <button
                  type="button"
                  className={`dash-fz-card${sel ? ' selected' : ''}`}
                  onClick={() => toggleFahrzeug(fz.id)}
                >
                  <div className="dash-fz-card-head">
                    <span className="dash-fz-card-name">{fz.name}</span>
                    <Icon
                      name={sel ? 'check_circle' : 'radio_button_unchecked'}
                      size={18}
                      color={sel ? '#fff' : 'var(--text-dim)'}
                    />
                  </div>
                  <div className="dash-fz-card-funkruf">{fz.funkruf || 'Funkruf —'}</div>
                </button>
                {sel && zuweisung && (
                  <PersonenEditor
                    personen={zuweisung.personen}
                    onChange={(p) => updatePersonen(fz.id, p)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 10 }}>
        <Btn type="submit" variant="primary" icon="save">{submitLabel || 'Speichern'}</Btn>
        <Btn type="button" variant="ghost" onClick={onCancel}>Abbrechen</Btn>
      </div>
    </form>
  );
};

const EinteilungCard = ({ einteilung, isActive, fahrzeuge, onEdit, onDelete, onAktivieren, onDeaktivieren }) => (
  <Card style={{ padding: 24, marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
    {isActive && <div className="einsatz-card-stripe" style={{ background: 'var(--green)' }} />}
    <div className="einsatz-card-head">
      <span className={`et-status-pill ${isActive ? 'live' : 'entwurf'}`}>
        <span className="et-status-dot" />
        {isActive ? 'Live' : 'Entwurf'}
      </span>
      <span className="einsatz-card-time">
        {new Date(einteilung.createdAt).toLocaleDateString('de-DE')}
      </span>
    </div>
    <h3 className="einsatz-card-title">{einteilung.name}</h3>
    {einteilung.beschreibung && <div className="einsatz-card-address">{einteilung.beschreibung}</div>}
    <div className="einsatz-card-chips" style={{ marginTop: 10 }}>
      {einteilung.fahrzeugZuweisungen.map(z => {
        const fz = fahrzeuge.find(f => f.id === z.fahrzeugId);
        if (!fz) return null;
        return (
          <span key={z.fahrzeugId} className="einsatz-chip">
            {fz.name}
            {z.personen.filter(p => p.name).length > 0 && (
              <span style={{ marginLeft: 4, opacity: 0.6, fontSize: '11px' }}>
                {z.personen.filter(p => p.name).length}P
              </span>
            )}
          </span>
        );
      })}
      {einteilung.fahrzeugZuweisungen.length === 0 && (
        <span className="einsatz-chip" style={{ opacity: 0.5 }}>Keine Fahrzeuge</span>
      )}
    </div>
    <div className="einsatz-card-actions">
      {!isActive ? (
        <Btn variant="primary" icon="cast" onClick={onAktivieren}>Live schalten</Btn>
      ) : (
        <Btn variant="success" icon="cast_connected" onClick={onDeaktivieren}>Live · Deaktivieren</Btn>
      )}
      <Btn variant="secondary" icon="edit" onClick={onEdit}>Bearbeiten</Btn>
      <Btn variant="ghost" icon="delete" onClick={onDelete} style={{ marginLeft: 'auto' }}>Löschen</Btn>
    </div>
  </Card>
);

const EinteilungenPage = ({ einteilungen, activeEinteilungId, fahrzeuge, actions }) => {
  const [showNew, setShowNew] = useState(false);
  const [editId, setEditId] = useState(null);

  return (
    <Section
      title="Fahrzeugeinteilungen"
      subtitle="Besatzungen vorbereiten und live auf dem Monitor anzeigen"
      action={
        !showNew && !editId && (
          <Btn variant="primary" icon="add" onClick={() => setShowNew(true)}>
            Neue Einteilung
          </Btn>
        )
      }
    >
      {showNew && (
        <EinteilungForm
          fahrzeuge={fahrzeuge}
          onSave={async (data) => { await actions.addEinteilung(data); setShowNew(false); }}
          onCancel={() => setShowNew(false)}
          submitLabel="Einteilung erstellen"
        />
      )}

      {einteilungen.length === 0 && !showNew && (
        <Card style={{ padding: 32, textAlign: 'center' }}>
          <div className="dash-empty-state">
            <Icon name="assignment_ind" size={40} color="var(--text-dim)" />
            <div style={{ marginTop: 8 }}>Noch keine Einteilungen vorhanden.</div>
            <div style={{ fontSize: 13, color: 'var(--text-mute)', marginTop: 4 }}>
              Erstelle eine Einteilung, weise Fahrzeuge und Personen zu und schalte sie live.
            </div>
          </div>
        </Card>
      )}

      {einteilungen.map(et => (
        editId === et.id ? (
          <EinteilungForm
            key={et.id}
            initial={et}
            fahrzeuge={fahrzeuge}
            onSave={async (data) => { await actions.editEinteilung(et.id, data); setEditId(null); }}
            onCancel={() => setEditId(null)}
            submitLabel="Änderungen speichern"
          />
        ) : (
          <EinteilungCard
            key={et.id}
            einteilung={et}
            isActive={et.id === activeEinteilungId}
            fahrzeuge={fahrzeuge}
            onEdit={() => setEditId(et.id)}
            onDelete={() => actions.deleteEinteilung(et.id)}
            onAktivieren={() => actions.aktivierenEinteilung(et.id)}
            onDeaktivieren={() => actions.deaktivierenEinteilung(et.id)}
          />
        )
      ))}
    </Section>
  );
};

// ─── Dashboard (top-level) ──────────────────────────────────────────────────
const Dashboard = () => {
  const [page, setPage] = useState('einsaetze');
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [einsaetze, setEinsaetze] = useState([]);
  const [termine, setTermine] = useState([]);
  const [einteilungen, setEinteilungen] = useState([]);
  const [activeEinteilungId, setActiveEinteilungId] = useState(null);
  const [kartenLocation, setKartenLocation] = useState({ lat: 50.9967, lng: 6.5711, zoom: 13 });

  useEffect(() => {
    fetchFahrzeuge();
    fetchEinsaetze();
    fetchTermine();
    fetchEinteilungen();
    fetchKartenLocation();

    const socket = io();
    socket.on('fahrzeugeUpdated', (u) => setFahrzeuge(u));
    socket.on('einsatzErstellt', (e) =>
      setEinsaetze((prev) => (prev.some((x) => x.id === e.id) ? prev : [...prev, e]))
    );
    socket.on('einsatzAusgeloest', (e) => setEinsaetze((prev) => prev.map((x) => (x.id === e.id ? e : x))));
    socket.on('einsatzBeendet', (e) => setEinsaetze((prev) => prev.map((x) => (x.id === e.id ? e : x))));
    socket.on('einsatzAktualisiert', (e) => setEinsaetze((prev) => prev.map((x) => (x.id === e.id ? e : x))));
    socket.on('einsatzGeloescht', (id) => setEinsaetze((prev) => prev.filter((x) => x.id !== id)));
    socket.on('termineUpdated', (t) => setTermine(t));
    socket.on('kartenLocationUpdated', (loc) => setKartenLocation(loc));
    socket.on('einsaetzeReset', (all) => setEinsaetze(all));
    socket.on('einteilungenUpdated', ({ einteilungen: e, activeEinteilungId: aid }) => {
      setEinteilungen(e);
      setActiveEinteilungId(aid);
    });
    socket.on('einteilungAktiviert', ({ einteilung, activeEinteilungId: aid }) => {
      setEinteilungen(prev => prev.map(x => x.id === einteilung.id ? einteilung : { ...x, status: 'entwurf' }));
      setActiveEinteilungId(aid);
    });
    socket.on('einteilungDeaktiviert', ({ id }) => {
      setEinteilungen(prev => prev.map(x => x.id === id ? { ...x, status: 'entwurf' } : x));
      setActiveEinteilungId(null);
    });

    return () => socket.close();
  }, []);

  const fetchFahrzeuge = async () => {
    const r = await axios.get('/api/fahrzeuge');
    setFahrzeuge(r.data);
  };
  const fetchEinsaetze = async () => {
    const r = await axios.get('/api/einsaetze');
    setEinsaetze(r.data);
  };
  const fetchTermine = async () => {
    const r = await axios.get('/api/termine');
    setTermine(r.data);
  };
  const fetchKartenLocation = async () => {
    const r = await axios.get('/api/karten-location');
    setKartenLocation(r.data);
  };
  const fetchEinteilungen = async () => {
    const r = await axios.get('/api/einteilungen');
    setEinteilungen(r.data.einteilungen);
    setActiveEinteilungId(r.data.activeEinteilungId);
  };

  // Actions
  const actions = {
    ausloesen: async (id) => {
      try { await axios.post(`/api/einsaetze/${id}/ausloesen`); } catch (e) { alert('Fehler: ' + e.message); }
    },
    beenden: async (id) => {
      try { await axios.post(`/api/einsaetze/${id}/beenden`); } catch (e) { alert('Fehler: ' + e.message); }
    },
    erneut: async (id) => {
      try { await axios.post(`/api/einsaetze/${id}/erneut-ausloesen`); } catch (e) { alert('Fehler: ' + e.message); }
    },
    delete: async (id) => {
      if (!window.confirm('Diesen Einsatz wirklich löschen?')) return;
      try { await axios.delete(`/api/einsaetze/${id}`); } catch (e) { alert('Fehler: ' + e.message); }
    },
    pdf: async (id) => {
      try {
        const r = await axios.get(`/api/einsaetze/${id}/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `alarmfax-${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) { alert('PDF-Download fehlgeschlagen.'); }
    },
    editEinsatz: async (id, data) => {
      try { await axios.put(`/api/einsaetze/${id}`, data); } catch (e) { alert('Fehler: ' + e.message); }
    },
    create: async (data) => {
      try {
        await axios.post('/api/einsaetze', data);
        setPage('einsaetze');
      } catch (e) { alert('Fehler beim Erstellen: ' + e.message); }
    },
    addFahrzeug: async ({ name, funkruf }) => {
      try { await axios.post('/api/fahrzeuge', { name, funkruf }); } catch (e) { alert('Fehler: ' + e.message); }
    },
    editFahrzeug: async (id, data) => {
      try { await axios.put(`/api/fahrzeuge/${id}`, data); } catch (e) { alert('Fehler: ' + e.message); }
    },
    deleteFahrzeug: async (id) => {
      if (!window.confirm('Dieses Fahrzeug wirklich löschen?')) return;
      try { await axios.delete(`/api/fahrzeuge/${id}`); } catch (e) { alert('Fehler: ' + e.message); }
    },
    saveKartenLocation: async (loc) => {
      await axios.post('/api/karten-location', loc);
    },
    addTermin: async (data) => {
      try { await axios.post('/api/termine', data); } catch (e) { alert('Fehler: ' + e.message); }
    },
    editTermin: async (id, data) => {
      try { await axios.put(`/api/termine/${id}`, data); } catch (e) { alert('Fehler: ' + e.message); }
    },
    deleteTermin: async (id) => {
      if (!window.confirm('Diesen Termin wirklich löschen?')) return;
      try { await axios.delete(`/api/termine/${id}`); } catch (e) { alert('Fehler: ' + e.message); }
    },
    addEinteilung: async (data) => {
      try { await axios.post('/api/einteilungen', data); } catch (e) { alert('Fehler: ' + e.message); }
    },
    editEinteilung: async (id, data) => {
      try { await axios.put(`/api/einteilungen/${id}`, data); } catch (e) { alert('Fehler: ' + e.message); }
    },
    deleteEinteilung: async (id) => {
      if (!window.confirm('Diese Einteilung wirklich löschen?')) return;
      try { await axios.delete(`/api/einteilungen/${id}`); } catch (e) { alert('Fehler: ' + e.message); }
    },
    aktivierenEinteilung: async (id) => {
      try { await axios.post(`/api/einteilungen/${id}/aktivieren`); } catch (e) { alert('Fehler: ' + e.message); }
    },
    deaktivierenEinteilung: async (id) => {
      try { await axios.post(`/api/einteilungen/${id}/deaktivieren`); } catch (e) { alert('Fehler: ' + e.message); }
    },
  };

  const aktiveCount = einsaetze.filter((e) => e.status === 'aktiv').length;
  const erstellteCount = einsaetze.filter((e) => e.status === 'erstellt').length;

  return (
    <div className="dashboard-root">
      <Sidebar
        active={page}
        onSelect={setPage}
        badges={{ einsaetze: aktiveCount + erstellteCount }}
      />
      <main className="dash-main">
        <Topbar
          aktiveCount={aktiveCount}
          erstellteCount={erstellteCount}
          fahrzeuge={fahrzeuge}
        />
        <div className="dash-content">
          {page === 'einsaetze' && (
            <EinsaetzePage
              einsaetze={einsaetze}
              fahrzeuge={fahrzeuge}
              kartenLocation={kartenLocation}
              onNew={() => setPage('neu')}
              actions={actions}
            />
          )}
          {page === 'neu' && (
            <NeuerEinsatzPage
              fahrzeuge={fahrzeuge}
              kartenLocation={kartenLocation}
              onCreate={actions.create}
              onCancel={() => setPage('einsaetze')}
            />
          )}
          {page === 'termine' && (
            <TerminePage termine={termine} actions={actions} />
          )}
          {page === 'fahrzeuge' && (
            <FahrzeugePage fahrzeuge={fahrzeuge} kartenLocation={kartenLocation} actions={actions} />
          )}
          {page === 'karte' && (
            <KartePage kartenLocation={kartenLocation} onSave={actions.saveKartenLocation} />
          )}
          {page === 'audio' && <AudioPage />}
          {page === 'einteilungen' && (
            <EinteilungenPage
              einteilungen={einteilungen}
              activeEinteilungId={activeEinteilungId}
              fahrzeuge={fahrzeuge}
              actions={actions}
            />
          )}
          {page === 'importexport' && (
            <ImportExportPage
              fahrzeuge={fahrzeuge}
              einsaetze={einsaetze}
              termine={termine}
              einteilungen={einteilungen}
              onImportSuccess={() => { fetchFahrzeuge(); fetchEinsaetze(); fetchTermine(); fetchEinteilungen(); }}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
